import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  ShoppingCart,
  IndianRupee,
  Clock,
  Search,
  ArrowUpRight,
  MoreHorizontal,
  FileText,
  Printer,
  Truck,
  ScanLine,
  PackageCheck,
  Send,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ordersService } from '@/services/orders';
import { invoicesService } from '@/services/invoices';
import { dispatchService } from '@/services/dispatch';
import type { OrderPaymentStatus, SalesOrder, SalesOrderStatus } from '@/types/orders';
import type { DispatchStatus } from '@/types/dispatch';
import { fmt } from '@/lib/format';
import { printHtmlInWindow } from '@/lib/print';
import { useDebounced } from '@/hooks/useDebounced';
import { OrderDetailSheet } from './OrderDetailSheet';
import { OrderFormSheet } from './OrderFormSheet';
import {
  orderActionState,
  canGenerateInvoice,
  canPrintInvoice,
  canMarkReadyForDispatch,
  canMarkDispatched,
  canPackVerify,
  canPrintLabel,
} from './orderActions';
import { MarkAsTestMenuItem } from '@/components/test-data/MarkAsTestMenuItem';
import { TestBadge } from '@/components/test-data/TestBadge';
import { useTestMarks } from '@/components/test-data/useTestMarks';

type StatusFilter = 'all' | SalesOrderStatus;

// Plain-English status labels — written for business users, not
// developers. No abbreviations, no enum-speak.
const STATUS_LABEL: Record<SalesOrderStatus, string> = {
  DRAFT: 'Draft',
  CREATED: 'Order placed',
  STOCK_RESERVED: 'Stock reserved',
  PAYMENT_PENDING: 'Payment pending',
  PAYMENT_CONFIRMED: 'Payment confirmed',
  READY_FOR_DISPATCH: 'Ready to dispatch',
  DISPATCHED: 'Dispatched',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<SalesOrderStatus, 'default' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'default',
  CREATED: 'default',
  STOCK_RESERVED: 'default',
  PAYMENT_PENDING: 'warning',
  PAYMENT_CONFIRMED: 'success',
  READY_FOR_DISPATCH: 'success',
  DISPATCHED: 'success',
  DELIVERED: 'success',
  CANCELLED: 'destructive',
};

// Green/amber/red payment chip — derived `paymentStatus` from the BE.
const PAYMENT_BADGE: Record<
  OrderPaymentStatus,
  { tone: 'success' | 'warning' | 'destructive'; label: string }
> = {
  PAID: { tone: 'success', label: 'Paid' },
  PARTIAL: { tone: 'warning', label: 'Part-paid' },
  UNPAID: { tone: 'destructive', label: 'Unpaid' },
};

// Forward-dispatch (shipment) status — surfaced as the Shipment column so
// a tenant admin sees real shipment progress, and a FAILED courier
// booking, without opening the order. Mirrors the dispatch detail labels.
const SHIP_LABEL: Record<DispatchStatus, string> = {
  PENDING: 'Pending pack',
  READY_FOR_PICKUP: 'Awaiting pickup',
  PICKED: 'Picked',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  FAILED: 'Dispatch failed',
  RETURN_INITIATED: 'Return initiated',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
};

const SHIP_TONE: Record<DispatchStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  PENDING: 'muted',
  READY_FOR_PICKUP: 'warning',
  PICKED: 'warning',
  IN_TRANSIT: 'success',
  OUT_FOR_DELIVERY: 'success',
  DELIVERED: 'success',
  FAILED: 'destructive',
  RETURN_INITIATED: 'warning',
  RETURNED: 'muted',
  CANCELLED: 'muted',
};

export function OrdersPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<SalesOrder | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const { isMarked } = useTestMarks();
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['orders', { q: debounced }],
    queryFn: () => ordersService.list({ search: debounced || undefined, pageSize: 200 }),
  });

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      DRAFT: 0,
      CREATED: 0,
      STOCK_RESERVED: 0,
      PAYMENT_PENDING: 0,
      PAYMENT_CONFIRMED: 0,
      READY_FOR_DISPATCH: 0,
      DISPATCHED: 0,
      DELIVERED: 0,
      CANCELLED: 0,
    };
    for (const o of allRows) c[o.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((o) => o.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const open = allRows.filter(
      (i) => !['DISPATCHED', 'DELIVERED', 'CANCELLED'].includes(i.status),
    );
    return {
      openCount: open.length,
      openValue: open.reduce((s, i) => s + Number(i.totalAmount ?? 0), 0),
      paymentPendingCount: counts.PAYMENT_PENDING,
    };
  }, [allRows, counts.PAYMENT_PENDING]);

  // Tab strip — only show statuses that have at least one row, plus the
  // common ones, to keep the strip from sprawling on a fresh tenant.
  const tabs: StatusTabOption[] = [
    { value: 'all',                label: 'All',                count: counts.all },
    { value: 'CREATED',            label: 'Order placed',       count: counts.CREATED },
    { value: 'PAYMENT_PENDING',    label: 'Payment pending',    count: counts.PAYMENT_PENDING },
    { value: 'READY_FOR_DISPATCH', label: 'Ready to dispatch',  count: counts.READY_FOR_DISPATCH },
    { value: 'DISPATCHED',         label: 'Dispatched',         count: counts.DISPATCHED },
    { value: 'DELIVERED',          label: 'Delivered',          count: counts.DELIVERED },
    { value: 'CANCELLED',          label: 'Cancelled',          count: counts.CANCELLED },
  ];

  const columns: Column<SalesOrder>[] = [
    {
      key: 'order',
      header: 'Order #',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[12px] font-medium text-foreground">{r.orderNumber}</span>
            {isMarked('SALES_ORDER', r.id) && <TestBadge />}
          </div>
          <div className="text-[10.5px] text-muted-foreground">
            {r.source === 'PORTAL' ? 'Portal' : 'Internal'} order
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Order date',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{r.customer.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.customer.code}</div>
        </div>
      ),
    },
    {
      key: 'warehouse',
      header: 'Warehouse',
      width: '160px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="text-[12.5px] text-foreground">{r.warehouse.name ?? r.warehouse.code}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.warehouse.code}</div>
        </div>
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      width: '70px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {r.items.length || '—'}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      width: '120px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">
          {fmt.money(r.totalAmount, r.currency)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Order status',
      width: '150px',
      cell: (r) => (
        <Badge
          variant={STATUS_TONE[r.status] === 'default' ? 'muted' : STATUS_TONE[r.status]}
          className="text-[10px]"
        >
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'fulfilment',
      header: 'Shipment',
      width: '160px',
      cell: (r) => {
        const f = r.fulfilment;
        if (!f) {
          return (
            <span
              className="text-[11px] text-muted-foreground"
              title="No dispatch created for this order yet"
            >
              —
            </span>
          );
        }
        return (
          <div className="space-y-0.5">
            <Badge variant={SHIP_TONE[f.status]} className="text-[10px]">
              {SHIP_LABEL[f.status]}
            </Badge>
            {f.trackingNumber && (
              <div
                className="font-mono text-[10px] text-muted-foreground"
                title="AWB / tracking number"
              >
                {f.trackingNumber}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'payment',
      header: 'Payment',
      width: '150px',
      cell: (r) => {
        const pb = PAYMENT_BADGE[r.paymentStatus] ?? PAYMENT_BADGE.UNPAID;
        return (
          <div className="flex flex-wrap items-center gap-1">
            <Badge
              variant={pb.tone}
              className="text-[9.5px]"
              title="Payment captured against this order"
            >
              {pb.label}
            </Badge>
            <Badge
              variant="muted"
              className="text-[9.5px]"
              title={
                r.paymentMode === 'COD'
                  ? 'Cash on delivery — collected by courier'
                  : 'Prepaid'
              }
            >
              {r.paymentMode === 'COD' ? 'COD' : 'Prepaid'}
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <OrderQuickActions
            order={r}
            onOpen={() => setSelected(r)}
            isTestMarked={isMarked('SALES_ORDER', r.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Sales Orders"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              New order
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard
            label="Open orders"
            value={fmt.number(summary.openCount)}
            tone="primary"
            icon={ShoppingCart}
          />
          <KpiCard
            label="Open value"
            value={fmt.inr(summary.openValue)}
            tone="default"
            icon={IndianRupee}
          />
          <KpiCard
            label="Payment pending"
            value={fmt.number(summary.paymentPendingCount)}
            tone={summary.paymentPendingCount ? 'warning' : 'default'}
            icon={Clock}
          />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order # …"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No orders yet'
                : `No ${STATUS_LABEL[filter as SalesOrderStatus].toLowerCase()} orders`
            }
            emptyDescription={
              filter === 'all' ? 'Book your first order to start the cycle.' : 'Try a different status filter.'
            }
            emptyAction={
              filter === 'all' ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  New order
                </Button>
              ) : null
            }
          />
        )}

        <OrderDetailSheet
          order={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />

        <OrderFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    </TooltipProvider>
  );
}

/**
 * Per-row quick actions for the orders grid. Every action is gated on
 * the order's REAL business state via the shared order-action policy
 * (orderActions.ts) — status, payment, and the BE-derived `invoice` /
 * `fulfilment` summaries — so an action only appears when it's actually
 * valid (an unpaid prepaid order shows neither invoice nor dispatch),
 * never an always-open menu that 400s on click:
 *
 *   • Generate invoice — not CANCELLED/DRAFT and no live invoice yet
 *   • Print invoice    — a live invoice exists
 *   • Mark ready       — status permits READY_FOR_DISPATCH
 *   • Pack & verify    — a forward dispatch exists and is still PENDING
 *   • Print label      — the dispatch has a booked AWB (trackingNumber)
 *   • Mark dispatched  — status is READY_FOR_DISPATCH
 *
 * Input-needing / destructive actions (take payment, cancel) stay in the
 * detail sheet where their forms + confirmations live.
 */
function OrderQuickActions({
  order,
  onOpen,
  isTestMarked,
}: {
  order: SalesOrder;
  onOpen: () => void;
  isTestMarked: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { fulfilment: dispatch, invoice } = order;

  // All gating goes through the shared order-action policy (orderActions
  // .ts) so the grid and the detail sheet never disagree, and payment is
  // respected — e.g. an unpaid PREPAID order offers neither invoice nor
  // mark-ready. COD is exempt (collected on delivery).
  const state = orderActionState(order);
  const showGenerateInvoice = canGenerateInvoice(state);
  const showPrintInvoice = canPrintInvoice(state);
  const showMarkReady = canMarkReadyForDispatch(state);
  const showMarkDispatched = canMarkDispatched(state);
  const showScan = canPackVerify(state);
  const showPrintLabel = canPrintLabel(state);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const openPrintable = (html: string, w: number, h: number) => {
    if (!printHtmlInWindow(html, { width: w, height: h })) {
      toast.error('Popup blocked. Allow popups to print.');
    }
  };

  const generateInvoice = useMutation({
    mutationFn: () => invoicesService.generate({ salesOrderId: order.id }),
    onSuccess: () => {
      toast.success('Invoice generated');
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Could not generate invoice'),
  });

  const printInvoice = useMutation({
    mutationFn: (id: string) => invoicesService.print(id),
    onSuccess: (p) => openPrintable(p.html, 800, 1000),
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Could not print invoice'),
  });

  // Chain createLabel (idempotent on dispatch) → printLabel → window,
  // exactly like the dispatch detail sheet's print button.
  const printLabel = useMutation({
    mutationFn: async () => {
      const label = await dispatchService.createLabel(dispatch!.dispatchId, {});
      return dispatchService.printLabel(label.id);
    },
    onSuccess: (p) => openPrintable(p.html, 600, 800),
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Could not print label'),
  });

  const transition = useMutation({
    mutationFn: (next: SalesOrderStatus) => ordersService.updateStatus(order.id, { status: next }),
    onSuccess: (_d, next) => {
      toast.success(
        next === 'READY_FOR_DISPATCH' ? 'Marked ready for dispatch' : 'Marked dispatched',
      );
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Could not update order'),
  });

  const busy =
    generateInvoice.isPending ||
    printInvoice.isPending ||
    printLabel.isPending ||
    transition.isPending;

  const hasQuickActions =
    showGenerateInvoice ||
    showPrintInvoice ||
    showMarkReady ||
    showScan ||
    showPrintLabel ||
    showMarkDispatched;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          disabled={busy}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onOpen}>
          <ArrowUpRight className="h-3.5 w-3.5" />
          Open detail
        </DropdownMenuItem>

        {hasQuickActions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10.5px] font-normal uppercase tracking-wide text-muted-foreground">
              Quick actions
            </DropdownMenuLabel>
            {showGenerateInvoice && (
              <DropdownMenuItem
                onClick={() => generateInvoice.mutate()}
                disabled={generateInvoice.isPending}
              >
                <FileText className="h-3.5 w-3.5" />
                Generate invoice
              </DropdownMenuItem>
            )}
            {showPrintInvoice && (
              <DropdownMenuItem
                onClick={() => printInvoice.mutate(invoice!.id)}
                disabled={printInvoice.isPending}
              >
                <Printer className="h-3.5 w-3.5" />
                Print invoice
              </DropdownMenuItem>
            )}
            {showMarkReady && (
              <DropdownMenuItem
                onClick={() => transition.mutate('READY_FOR_DISPATCH')}
                disabled={transition.isPending}
              >
                <PackageCheck className="h-3.5 w-3.5" />
                Mark ready for dispatch
              </DropdownMenuItem>
            )}
            {showScan && (
              <DropdownMenuItem onClick={() => navigate(`/dispatch?id=${dispatch!.dispatchId}`)}>
                <ScanLine className="h-3.5 w-3.5" />
                Pack &amp; verify
              </DropdownMenuItem>
            )}
            {showPrintLabel && (
              <DropdownMenuItem onClick={() => printLabel.mutate()} disabled={printLabel.isPending}>
                <Truck className="h-3.5 w-3.5" />
                Print shipping label
              </DropdownMenuItem>
            )}
            {showMarkDispatched && (
              <DropdownMenuItem
                onClick={() => transition.mutate('DISPATCHED')}
                disabled={transition.isPending}
              >
                <Send className="h-3.5 w-3.5" />
                Mark dispatched
              </DropdownMenuItem>
            )}
          </>
        )}

        <DropdownMenuSeparator />
        <MarkAsTestMenuItem entityType="SALES_ORDER" entityId={order.id} marked={isTestMarked} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
